const TAU = 0.5;
const EPS = 0.000001;
const SCALE = 173.7178;

export const DEFAULT_RATING = 1500;
export const DEFAULT_RD = 350;
export const DEFAULT_VOL = 0.06;

function g(phi) {
  return 1 / Math.sqrt(1 + (3 * phi * phi) / (Math.PI * Math.PI));
}

// Single-game Glicko-2 update. score: 1 win / 0.5 draw / 0 loss.
// player & opponent: { rating, rd, vol }. Returns new { rating, rd, vol }.
export function glicko2Update(player, opponent, score) {
  const mu = (player.rating - DEFAULT_RATING) / SCALE;
  const phi = player.rd / SCALE;
  const sigma = player.vol;
  const muJ = (opponent.rating - DEFAULT_RATING) / SCALE;
  const phiJ = opponent.rd / SCALE;

  const gj = g(phiJ);
  const e = 1 / (1 + Math.exp(-gj * (mu - muJ)));
  const v = 1 / (gj * gj * e * (1 - e));
  const delta = v * gj * (score - e);

  // Volatility via Illinois root-finding (Glickman step 5)
  const a = Math.log(sigma * sigma);
  const f = (x) =>
    (Math.exp(x) * (delta * delta - phi * phi - v - Math.exp(x))) /
      (2 * Math.pow(phi * phi + v + Math.exp(x), 2)) -
    (x - a) / (TAU * TAU);

  let A = a;
  let B;
  if (delta * delta > phi * phi + v) {
    B = Math.log(delta * delta - phi * phi - v);
  } else {
    let k = 1;
    while (f(a - k * TAU) < 0) k += 1;
    B = a - k * TAU;
  }
  let fA = f(A);
  let fB = f(B);
  let iter = 0;
  while (Math.abs(B - A) > EPS && iter < 100) {
    const C = A + ((A - B) * fA) / (fB - fA);
    const fC = f(C);
    if (fC * fB <= 0) {
      A = B;
      fA = fB;
    } else {
      fA = fA / 2;
    }
    B = C;
    fB = fC;
    iter += 1;
  }
  const newSigma = Math.exp(A / 2);

  const phiStar = Math.sqrt(phi * phi + newSigma * newSigma);
  const newPhi = 1 / Math.sqrt(1 / (phiStar * phiStar) + 1 / v);
  const newMu = mu + newPhi * newPhi * gj * (score - e);

  return {
    rating: SCALE * newMu + DEFAULT_RATING,
    rd: SCALE * newPhi,
    vol: newSigma,
  };
}
